/**
 * SimpleTabs - lightweight, dependency-free tabs system
 * - Initializes tabs based on standard markup (ul>li>a[href="#panel"] + panels with matching IDs)
 * - Adds ARIA attributes and keyboard navigation
 * - Provides API: Tabs.init(selector|NodeList|Element), new Tabs(container)
 * - Exposes global `SimpleTabs`
 */
(function (global) {
    'use strict';

    function each(list, fn) {
        Array.prototype.forEach.call(list, fn);
    }

    function toArray(list) { return Array.prototype.slice.call(list); }

    function findClosestTabLink(el) {
        while (el && el !== document.documentElement) {
            if (el.tagName && el.tagName.toLowerCase() === 'a' && el.getAttribute('href') && el.getAttribute('href').indexOf('#') === 0) return el;
            el = el.parentElement;
        }
        return null;
    }

    function Tabs(container, options) {
        this.container = container;
        this.options = options || {};
        this.init();
    }

    Tabs.prototype.init = function () {
        var container = this.container;

        // Find tab list (ul or nav) - prefer element with role="tablist" or .tabs-nav or the first ul inside container
        this.tablist = container.querySelector('[role="tablist"]') || container.querySelector('.tabs-nav') || container.querySelector('ul');
        if (!this.tablist) return;

        // Collect tab links (a[href="#id"]) inside list items
        this.tabLinks = toArray(this.tablist.querySelectorAll('a[href^="#"]'));
        this.panels = this.tabLinks.map(function (a) {
            var id = a.getAttribute('href').slice(1);
            return document.getElementById(id);
        });

        // Accessibility: set roles and attributes
        this.tablist.setAttribute('role', 'tablist');
        this.tabLinks.forEach(function (link, i) {
            var li = link.closest('li');
            if (li) li.classList.add('tabs-tab');
            link.setAttribute('role', 'tab');
            link.setAttribute('aria-selected', 'false');
            link.setAttribute('tabindex', '-1');
            var id = link.getAttribute('href').slice(1);
            link.setAttribute('aria-controls', id);
            if (!link.id) link.id = 'tab-' + Math.random().toString(36).slice(2, 9);
        });

        this.panels.forEach(function (panel) {
            if (!panel) return;
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('tabindex', '0');
            // hide by default
            panel.style.display = 'none';
        });

        // Determine initial active tab (data-active or class .active or first)
        var activeIndex = 0;
        for (var i = 0; i < this.tabLinks.length; i++) {
            var link = this.tabLinks[i];
            if (link.classList.contains('active') || link.parentElement && link.parentElement.classList.contains('active')) { activeIndex = i; break; }
            if (link.dataset && link.dataset.active === 'true') { activeIndex = i; break; }
        }

        this.active = -1;
        this.activate(activeIndex);

        // Click handlers
        var self = this;
        this._clickHandler = function (ev) {
            var a = findClosestTabLink(ev.target);
            if (!a) return;
            ev.preventDefault();
            var idx = self.tabLinks.indexOf(a);
            if (idx >= 0) self.activate(idx);
        };

        this._keyHandler = function (ev) {
            var k = ev.key || ev.keyCode;
            var activeIdx = self.active;
            if (['ArrowLeft','Left','ArrowUp','Up'].indexOf(k) !== -1 || k === 37 || k === 38) {
                ev.preventDefault();
                self.activate((activeIdx - 1 + self.tabLinks.length) % self.tabLinks.length);
            } else if (['ArrowRight','Right','ArrowDown','Down'].indexOf(k) !== -1 || k === 39 || k === 40) {
                ev.preventDefault();
                self.activate((activeIdx + 1) % self.tabLinks.length);
            } else if (k === 'Home' || k === 36) {
                ev.preventDefault(); self.activate(0);
            } else if (k === 'End' || k === 35) {
                ev.preventDefault(); self.activate(self.tabLinks.length - 1);
            }
        };

        this.tablist.addEventListener('click', this._clickHandler);
        this.tablist.addEventListener('keydown', this._keyHandler);

        // Support mobile nav elements inside container with class .hd-mot-nav-mobile (used in mande.modes-of-travel)
        this.container.querySelectorAll('.hd-mot-nav-mobile').forEach(function (el) {
            el.addEventListener('click', function (ev) {
                ev.preventDefault();
                var idx = parseInt(el.dataset.tabid, 10);
                if (!isNaN(idx)) self.activate(idx);
                // toggle active classes on mobile links
                self.container.querySelectorAll('.hd-mot-nav-mobile').forEach(function (m) { m.classList.remove('active'); });
                el.classList.add('active');
            });
        });
    };

    Tabs.prototype.activate = function (index) {
        index = parseInt(index, 10) || 0;
        if (index < 0) index = 0;
        if (index >= this.tabLinks.length) index = this.tabLinks.length - 1;
        if (index === this.active) return;

        // deactivate previous
        if (this.active >= 0) {
            var prevLink = this.tabLinks[this.active];
            var prevPanel = this.panels[this.active];
            if (prevLink) { prevLink.classList.remove('active'); prevLink.setAttribute('aria-selected','false'); prevLink.setAttribute('tabindex','-1'); }
            if (prevPanel) { prevPanel.classList.remove('active'); prevPanel.style.display = 'none'; }
            if (prevLink && prevLink.parentElement) prevLink.parentElement.classList.remove('active');
        }

        // activate new
        var link = this.tabLinks[index];
        var panel = this.panels[index];
        if (link) { link.classList.add('active'); link.setAttribute('aria-selected','true'); link.setAttribute('tabindex','0'); link.focus(); }
        if (panel) { panel.classList.add('active'); panel.style.display = ''; }
        if (link && link.parentElement) link.parentElement.classList.add('active');

        this.active = index;

        // dispatch custom event on container
        var ev = new CustomEvent('tabchange', { detail: { index: index, tab: link, panel: panel } });
        this.container.dispatchEvent(ev);
    };

    Tabs.prototype.destroy = function () {
        if (!this.tablist) return;
        this.tablist.removeEventListener('click', this._clickHandler);
        this.tablist.removeEventListener('keydown', this._keyHandler);
        // restore panels
        this.panels.forEach(function (p) { if (p) { p.style.display = ''; p.removeAttribute('role'); } });
    };

    // Static init helper
    Tabs.init = function (selectorOrList) {
        var nodes = [];
        if (!selectorOrList) selectorOrList = '.js-tabs, .hd-modes-of-travel, [data-tabs]';
        if (typeof selectorOrList === 'string') nodes = document.querySelectorAll(selectorOrList);
        else if (selectorOrList instanceof Element) nodes = [selectorOrList];
        else if (selectorOrList.length) nodes = selectorOrList;

        var instances = [];
        each(nodes, function (node) {
            if (!node) return;
            var inst = new Tabs(node);
            // store instance for later
            node._simpleTabs = inst;
            instances.push(inst);
        });
        return instances;
    };

    // Expose
    global.SimpleTabs = Tabs;

})(window);
